import { FilterSelect, SearchInput, SortIcon } from "@marketplace/shared/ui";
import { Center, ConditionalRendering, Loader, Text } from "@marketplace/ui";
import { DataTable } from "@marketplace/ui/data-table";
import { getRouteApi } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Pagination } from "@/components";

import {
  bclProductModalAtom,
  crmProductModalAtom,
  erfinaProductModalAtom
} from "../../atoms/integration-product-modal-atom";
import { BclProductModal } from "../../components/bcl-product-modal";
import { CrmProductModal } from "../../components/crm-product-modal";
import { ErfinaProductModal } from "../../components/erfina-product-modal";
import { useGetIntegrationProductListQuery } from "../../hooks/queries/use-get-integration-product-list-query";
import { useIntegrationProductListTableColumns } from "../../hooks/use-integration-product-list-table-columns";
import { useSortByOptions } from "../../hooks/use-sort-by-options";
import type { ProductListSortValue } from "../../types";

const route = getRouteApi("/products/integration");

export const IntegrationProductListPage = () => {
  const { page, q, sort } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("product");
  const { t: tCommon } = useTranslation("common");

  const [search, setSearch] = useState(q || "");
  const sortByOptions = useSortByOptions();

  const setCrmModal = useSetAtom(crmProductModalAtom);
  const setBclModal = useSetAtom(bclProductModalAtom);
  const setErfinaModal = useSetAtom(erfinaProductModalAtom);

  const columns = useIntegrationProductListTableColumns({
    onCrmClick: (data) => setCrmModal({ visible: true, data }),
    onBclClick: (data) => setBclModal({ visible: true, data }),
    onErfinaClick: (data) => setErfinaModal({ visible: true, data })
  });

  const { data, isLoading } = useGetIntegrationProductListQuery({
    queryParams: {
      page,
      limit: 10,
      q
    }
  });

  useEffect(() => {
    setSearch(q || "");
  }, [q]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      navigate({
        search: (prev) => ({
          ...prev,
          q: search,
          page: 1
        })
      });
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleSortByChange = (value: ProductListSortValue) => {
    navigate({
      search: (prev) => ({
        ...prev,
        sort: value,
        page: 1
      })
    });
  };

  const integrationData = data?.data;
  const metadata = data?.metadata;

  return (
    <>
      <div className="mb-6">
        <Text type="heading" size="l" className="text-humble-blue-500">
          {t("integrationProductListPage.title")}
        </Text>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div>
          <SearchInput
            className="lg:w-[634px]"
            placeholder={t("integrationProductListPage.searchPlaceholder")}
            onChange={handleSearchChange}
            value={search}
          />
        </div>
        <div className="flex gap-x-[14px]">
          <FilterSelect
            label={tCommon("sortBy")}
            options={sortByOptions}
            value={sort}
            onChange={(v) => handleSortByChange(v as ProductListSortValue)}
            width={179}
            icon={<SortIcon />}
            dropdownPosition="bottom-end"
          />
        </div>
      </div>

      <ConditionalRendering condition={isLoading}>
        <Center className="my-20">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering
        condition={!isLoading && integrationData && integrationData.length > 0}
      >
        <div className="space-y-6">
          <DataTable
            minHeight={10}
            columns={columns}
            records={integrationData ?? []}
          />

          <Pagination
            currentPageTotal={integrationData?.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={(value) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: value
                })
              })
            }
          />
        </div>
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoading && !integrationData?.length}>
        <div className="flex flex-col justify-center items-center gap-y-6 mt-[56px]">
          <Text type="heading" size="xl" className="mb-2 text-fjord-grey-500">
            {t("integrationProductListPage.emptyState.title")}
          </Text>
          <Text type="paragraph" size="l" className="text-fjord-grey-300">
            {t("integrationProductListPage.emptyState.description")}
          </Text>
        </div>
      </ConditionalRendering>

      {/* Integration Modals */}
      <CrmProductModal />
      <BclProductModal />
      <ErfinaProductModal />
    </>
  );
};
